"use client";

import { Icon } from "@fanduel/formation-react-components";
import { TimeCalendar } from "@fanduel/formation-react-icons";
import { Card, Dropdown } from "@fanduel/og-fe-library";
import { useEffect, useCallback, useState, useRef, ReactNode } from "react";
import { ScrollItem } from "@/components/common/common";
import ErrorComponent from "@/components/empty/ErrorComponent";
import LoadingBackdrop from "@/components/loadingBackdrop/LoadingBackdrop";
import TabsLoadMore from "@/components/TabsLoadMore";
import TabsPagination from "@/components/TabsPagination";
import useGetScores from "@/utils/hooks/useGetScores";
import useScrollRefs from "@/utils/hooks/useScrollRefs";
import useSports from "@/utils/hooks/useSports";
import { filterEmpty } from "@/utils/index";
import SadCalendar from "../svg/SadCalendar";
import GameCard from "./GameCard";
import styles from "./scoreboard.module.scss";

type Overrides = Record<string, React.ReactNode>;

interface ScoreBoardContainerProps {
  sport?: string; // The sport to initialize as selected
  titleOverrides?: Overrides;
}

const ScoreBoardContainer = ({
  sport,
  titleOverrides,
}: ScoreBoardContainerProps) => {
  const [selectedSport, setSelectedSport] = useState(sport);

  const { scores, isLoading } = useGetScores(selectedSport);
  const { selectableSportsWithScores, isLoading: isLoadingSports } =
    useSports();

  const [sportIndex, setSportIndex] = useState(0);
  const initializedRef = useRef(false);

  useEffect(() => {
    const canInitialize = !isLoadingSports && !initializedRef.current;

    if (canInitialize && sport) {
      const index = (selectableSportsWithScores ?? []).indexOf(
        selectedSport ?? ""
      );
      if (index > 0) {
        setSportIndex(index);
      }
      initializedRef.current = true;
    }
  }, [isLoadingSports, selectableSportsWithScores, selectedSport, sport]);

  const filteredGames = filterEmpty(scores ?? []);

  const [allActive, setAllActive] = useState(false);
  const mobileGameCt = 4;
  const offsetPixels = 378; // width of GameCard + padding
  const {
    containerRef,
    scrollRef,
    addOffset,
    removeOffset,
    disableScrollPlus,
    disableScrollMinus,
    scrollUpdate,
  } = useScrollRefs(offsetPixels, scores);

  const contentRender = useCallback(() => {
    if (isLoading) {
      return (
        <div className={styles.statusContainer}>
          <LoadingBackdrop loading={isLoading} />
        </div>
      );
    }

    if (filteredGames.length === 0) {
      return (
        <div className={styles.statusContainer}>
          <ErrorComponent
            image={<SadCalendar />}
            headline="Looks like the field is empty."
            subtext="No upcoming games scheduled."
            showCta={false}
          />
        </div>
      );
    }

    return (
      <div
        className={styles.itemContainer}
        onScroll={scrollUpdate}
        ref={containerRef}
      >
        <div className={styles.scrollContainer}>
          {filteredGames.map((score, index) => (
            <ScrollItem key={score.id} isHidden={!allActive && index > 3}>
              <GameCard {...score} />
            </ScrollItem>
          ))}
          <div className={styles.lastItem} ref={scrollRef} />
        </div>
      </div>
    );
  }, [
    isLoading,
    filteredGames,
    scrollRef,
    allActive,
    containerRef,
    scrollUpdate,
  ]);

  const loadMoreRender = useCallback(() => {
    if (filteredGames.length <= mobileGameCt) {
      return null;
    }

    return (
      <div className={styles.mobileToggle}>
        <TabsLoadMore
          allActive={allActive}
          showLessLabel="Show less games"
          showMoreLabel="Show all games"
          onClick={() => setAllActive((val) => !val)}
        />
      </div>
    );
  }, [allActive, filteredGames.length]);

  const onSelectSport = (sportIndexArg: number) => {
    const sportSelection = selectableSportsWithScores?.[sportIndexArg];
    setSelectedSport(sportSelection);
    setSportIndex(sportIndexArg);

    // Reset scroll position
    if (containerRef.current?.scrollLeft) {
      containerRef.current.scrollLeft = 0;
    }
  };

  let title: ReactNode = (
    <>
      <Icon size="sm" as={TimeCalendar} data-testid="icons.calendar" />
      Upcoming Games and Scoreboard
    </>
  );
  if (
    titleOverrides != null &&
    selectedSport != null &&
    selectedSport in titleOverrides
  ) {
    title = titleOverrides[selectedSport];
  }

  return (
    <Card variant="elevation" rounded="sm">
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <TabsPagination
          addOffset={addOffset}
          removeOffset={removeOffset}
          disableScrollPlus={disableScrollPlus}
          disableScrollMinus={disableScrollMinus}
        />
      </div>
      <div className={styles.dropDownContainer}>
        <Dropdown
          options={selectableSportsWithScores.map((sportLabel) => ({
            label: sportLabel,
          }))}
          selected={sportIndex}
          title="LEAGUE"
          setSelected={setSportIndex}
          onChange={onSelectSport}
        />
      </div>
      {contentRender()}
      {loadMoreRender()}
    </Card>
  );
};

export default ScoreBoardContainer;
